export type HealthResponse = {
  status: 'ok';
  service: string;
  version: string;
  timestamp: string;
};

export type AuthUser = {
  id: number | string;
  username: string;
  email: string | null;
  phone: string | null;
  language: 'cs' | 'en' | 'de';
  first_name: string | null;
  last_name: string | null;
};

export type Authorization = {
  direct_roles: string[];
  effective_roles: string[];
  capabilities: string[];
};

export type AuthContext = {
  user: AuthUser;
  authorization: Authorization;
};

export type UpdateOwnProfile = {
  current_password?: string;
  password?: string;
  password_confirmation?: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  language: 'cs' | 'en' | 'de';
};

export type UpdateOwnProfileResponse = AuthContext & {
  status: 'updated' | 'email_verification_required';
  masked_email?: string | null;
};

export type ManagedRoleSummary = { id: number; name: string };

export type ManagedUser = {
  id: number;
  username: string;
  language: 'cs' | 'en' | 'de';
  created_at: string;
  parent_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  roles: ManagedRoleSummary[];
};

export type ManagedRole = {
  id: number;
  name: string;
  parent_ids: number[];
  users_count: number;
  direct_capabilities: string[];
  effective_capabilities: string[];
  protected: boolean;
};

export type SaveManagedUser = {
  username: string;
  password?: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  language: 'cs' | 'en' | 'de';
  parent_id: number | null;
  role_id: number | null;
};

export type SaveManagedRole = { name: string; parent_ids: number[] };

export type Installation = {
  id: number;
  name: string;
  ip: string;
};

export type PushDevice = {
  id: number;
  installation_id: string;
  firebase_project: string;
  platform: 'web' | 'ios' | 'android';
  label: string | null;
  locale: 'cs' | 'en' | 'de';
  app_version: string | null;
  enabled: boolean;
  last_seen_at: string;
  last_success_at: string | null;
  last_failure_at?: string | null;
  last_failure_code?: string | null;
};

export type RegisterPushDevice = {
  installation_id: string;
  fcm_token: string;
  platform: PushDevice['platform'];
  label?: string | null;
  locale?: PushDevice['locale'];
  app_version?: string | null;
  enabled?: boolean;
};

export type PushArea = { aid: number; name: string; permission: 'full' | 'limited'; enabled: boolean };

export type NotificationPreferenceArea = {
  aid: number;
  name: string;
  permission: 'full' | 'limited';
  default_sender_value: boolean | null;
};

export type NotificationScheduleEntry = { aid: number; day: number; hour: number; active: boolean; mode?: 'none' | 'on' | 'off' | 'custom'; start_minute?: number; end_minute?: number };
export type NotificationPreferences = {
  available: boolean;
  can_manage_advanced: boolean;
  areas: NotificationPreferenceArea[];
  schedule: NotificationScheduleEntry[];
};

export type NeoNotificationMedia = {
  id: number;
  position: number;
  thumbnail_url: string;
  original_url: string | null;
};

export type NeoNotification = {
  id: number;
  delivery_id: number;
  type: string;
  title: string;
  body: string | null;
  aid: number;
  cid: number | null;
  occurred_at: string;
  status: 'queued' | 'sent' | 'failed';
  read_at: string | null;
  media: NeoNotificationMedia[];
};

export type SensorDefinition = { id: number; cid: number; type: string; bit: number | null; bit_state: number; output: number; current_state: number | null; set_cmd: string | null; clear_cmd: string | null };
export type CameraDefinition = { id: number; cid: number; [key: string]: string | number | null };
export type NeuronCamera = { id: number; name: string | null; description?: string | null; type: 'camera' | 'sensor'; sensor?: SensorDefinition | null; definition?: CameraDefinition | null };
export type NeuronRoom = { id: number; csid: number; name: string | null; description: string | null; created: string | null; updated: string | null; type: 'room'; cameras: NeuronCamera[] };
export type RoomBorderPoint = { id?: number; step?: number; x: number; y: number };
export type CameraMaskPoint = { id?: number; mid?: number; step?: number; pix_x: number; pix_y: number; usable: 0 | 1 | 2 };
export type CameraMask = { id: number; cid: number; type: number; value: number | null; name: string | null; description: string | null; points: CameraMaskPoint[] };
export type CameraPoint = { id?: number; cid?: number; p_type: number; abs_x: number; abs_y: number; abs_z: number; pix_x: number; pix_y: number };
export type NeuronPoeMode = 0 | 1 | 2 | 3;
export type NeuronLocalServer = { id: number; cs_id: number; name: string | null; description: string | null; ip_address: string | null; res_x: number; res_y: number };
export type NeuronTiming = { id: number; csid?: number; rid?: number; start: string; end: string; weekday: number };
export type SaveNeuronTiming = { weekday: number; start: string; end: string };
export type NeuronLayoutCamera = { id: number; name: string | null; room_name: string | null };
export type NeuronCameraLayoutItem = { id: number; ls_id: number; cid: number; pos_left: number; pos_top: number; width: number; height: number };
export type SaveNeuronCameraLayoutItem = Omit<NeuronCameraLayoutItem, 'id' | 'ls_id'>;
export type NeuronCameraLayout = { canvas: { width: number; height: number }; cameras: NeuronLayoutCamera[]; layout: NeuronCameraLayoutItem[] };
export type NeuronVirtualCameraItem = { id: number; master_cid: number; servant_cid: number; servant_name: string | null; left: number; top: number; width: number; height: number; weight: number };
export type NeuronVirtualCamera = { canvas: { width: number; height: number }; servants: NeuronLayoutCamera[]; layout: NeuronVirtualCameraItem[] };
export type CameraRecordSettings = { res_x: number; fps: number; quality: number };
export type CameraRecords = { hours: Record<number, boolean[]>; record: CameraRecordSettings; stream: CameraRecordSettings };
export type SaveCameraRecords = { hours: Record<number, boolean[]>; record: CameraRecordSettings; stream: CameraRecordSettings };
export type NeuronServer = { id: number; name: string | null; description: string | null; type: string | null; vpn_ip: string | null; poe_mode: NeuronPoeMode; apply_settings: number; local_servers: NeuronLocalServer[]; rooms: NeuronRoom[] };

export type ImageData = {
  id: number;
  cid: number;
  validation_class: number;
  date: string;
  image?: string | null;
  frames?: AlertFrame[];
};

export type AlertFrame = {
  id: number;
  class_id: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type AlertTrainingPayload = {
  image_id: number;
  action: 'save' | 'negative' | 'unknown';
  class_id: number;
  areas?: Array<{
    class_id: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
};

export type SuperCheckGroup = {
  class_id: number;
  count: number;
};

export type UserReportedError = {
  id: number;
  installation: string | null;
  occurred_at: string | null;
  ended_at: string | null;
  error_description: string;
  resolved: boolean;
  reporter_email: string | null;
};

export type VerificationRequiredResponse = {
  status: 'verification_required';
  masked_email: string;
  expires_in: number;
};

export type EmailSetupRequiredResponse = {
  status: 'email_setup_required';
  expires_in: number;
};

export type LoginResponse =
  | VerificationRequiredResponse
  | EmailSetupRequiredResponse;

export type VerifyResponse = {
  status: 'authenticated';
  user: AuthUser;
  token: string;
};

export type TokenDays = 1 | 7 | 30 | 90 | 365;
export type AuthTokenDetails = { created_at: string; expires_at: string; ttl_days: number };
export type AuthTokenResponse = { token: string | null; details?: AuthTokenDetails | null };

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors: Record<string, string[]> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let apiAuthToken: string | null = null;

export function setApiAuthToken(token: string | null): void {
  apiAuthToken = token;
}

function authHeaders(): Record<string, string> {
  return apiAuthToken ? { Authorization: `Bearer ${apiAuthToken}` } : {};
}

function apiUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, '')}/api/v1${path}`;
}

function readXsrfToken(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith('XSRF-TOKEN='));

  return cookie ? decodeURIComponent(cookie.slice('XSRF-TOKEN='.length)) : null;
}

async function parseApiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => ({}))) as {
    message?: string;
    errors?: Record<string, string[]>;
  };

  return new ApiError(
    body.message ?? `HTTP ${response.status}`,
    response.status,
    body.errors,
  );
}

async function csrfHeaders(
  baseUrl: string,
  locale: string,
): Promise<Record<string, string>> {
  const response = await fetch(apiUrl(baseUrl, '/auth/csrf'), {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Accept-Language': locale,
      ...authHeaders(),
    },
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const body = (await response.json().catch(() => ({}))) as { token?: string };
  if (body.token) {
    return { 'X-CSRF-TOKEN': body.token };
  }

  const cookieToken = readXsrfToken();
  return cookieToken ? { 'X-XSRF-TOKEN': cookieToken } : {};
}

async function mutateJson<T>(
  baseUrl: string,
  path: string,
  body?: unknown,
  locale = 'cs',
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
): Promise<T> {
  const xsrfHeaders = await csrfHeaders(baseUrl, locale);
  const response = await fetch(apiUrl(baseUrl, path), {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Accept-Language': locale,
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...xsrfHeaders,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function getJson<T>(baseUrl: string, path: string): Promise<T> {
  const response = await fetch(apiUrl(baseUrl, path), {
    credentials: 'include',
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (!response.ok) {
    throw await parseApiError(response);
  }
  return (await response.json()) as T;
}

export async function getHealth(
  baseUrl: string,
  signal?: AbortSignal,
): Promise<HealthResponse> {
  const response = await fetch(apiUrl(baseUrl, '/health'), {
    headers: {
      Accept: 'application/json',
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return (await response.json()) as HealthResponse;
}

export function getAuthContext(baseUrl: string): Promise<AuthContext> {
  return getJson<AuthContext>(baseUrl, '/auth/me');
}

export function updateOwnProfile(baseUrl: string, data: UpdateOwnProfile, locale = 'cs'): Promise<UpdateOwnProfileResponse> {
  return mutateJson<UpdateOwnProfileResponse>(baseUrl, '/auth/me', data, locale, 'PUT');
}

export function login(
  baseUrl: string,
  credentials: { login: string; password: string },
  locale = 'cs',
): Promise<LoginResponse> {
  return mutateJson<LoginResponse>(baseUrl, '/auth/login', credentials, locale);
}

export function setupEmail(
  baseUrl: string,
  email: string,
  emailConfirmation: string,
  locale = 'cs',
): Promise<VerificationRequiredResponse> {
  return mutateJson<VerificationRequiredResponse>(
    baseUrl,
    '/auth/setup-email',
    { email, email_confirmation: emailConfirmation },
    locale,
  );
}

export function verifyTwoFactor(
  baseUrl: string,
  code: string,
  tokenDays: TokenDays = 365,
  locale = 'cs',
): Promise<VerifyResponse> {
  return mutateJson<VerifyResponse>(baseUrl, '/auth/verify', { code, token_days: tokenDays }, locale);
}

export function getAuthToken(baseUrl: string): Promise<AuthTokenResponse> {
  return getJson<AuthTokenResponse>(baseUrl, '/auth/token');
}

export function issueAuthToken(baseUrl: string, tokenDays: TokenDays, locale = 'cs'): Promise<AuthTokenResponse> {
  return mutateJson<AuthTokenResponse>(baseUrl, '/auth/token', { token_days: tokenDays }, locale);
}

export function renewAuthToken(baseUrl: string, tokenDays: TokenDays, locale = 'cs'): Promise<AuthTokenResponse> {
  return mutateJson<AuthTokenResponse>(baseUrl, '/auth/token', { token_days: tokenDays }, locale, 'PATCH');
}

export function revokeAuthToken(baseUrl: string, locale = 'cs'): Promise<void> {
  return mutateJson<void>(baseUrl, '/auth/token', undefined, locale, 'DELETE');
}

export function logout(baseUrl: string): Promise<void> {
  return mutateJson<void>(baseUrl, '/auth/logout');
}

export async function getPushDevices(baseUrl: string): Promise<PushDevice[]> {
  return (await getJson<{ devices: PushDevice[] }>(baseUrl, '/notifications/devices')).devices;
}

export async function registerPushDevice(baseUrl: string, data: RegisterPushDevice, locale = 'cs'): Promise<PushDevice> {
  return (await mutateJson<{ device: PushDevice }>(baseUrl, '/notifications/devices', data, locale)).device;
}

export function removePushDevice(baseUrl: string, id: number, locale = 'cs'): Promise<void> {
  return mutateJson<void>(baseUrl, `/notifications/devices/${id}`, undefined, locale, 'DELETE');
}

export async function updatePushDevice(baseUrl: string, id: number, data: { label?: string | null; enabled?: boolean }, locale = 'cs'): Promise<PushDevice> {
  return (await mutateJson<{ device: PushDevice }>(baseUrl, `/notifications/devices/${id}`, data, locale, 'PATCH')).device;
}

export function sendTestPush(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: 'sent'; message_id: string }> {
  return mutateJson(baseUrl, `/notifications/devices/${id}/test`, undefined, locale);
}

export async function getPushDeviceAreas(baseUrl: string, id: number): Promise<PushArea[]> {
  return (await getJson<{ areas: PushArea[] }>(baseUrl, `/notifications/devices/${id}/areas`)).areas;
}

export function savePushDeviceAreas(baseUrl: string, id: number, areas: PushArea[], locale = 'cs'): Promise<{ areas: PushArea[] }> {
  return mutateJson(baseUrl, `/notifications/devices/${id}/areas`, { areas }, locale, 'PUT');
}

export function getNotificationPreferences(baseUrl: string, aid?: number): Promise<NotificationPreferences> {
  const query = aid === undefined ? '' : `?aid=${encodeURIComponent(aid)}`;
  return getJson<NotificationPreferences>(baseUrl, `/notifications/preferences${query}`);
}

export function saveNotificationPreferences(
  baseUrl: string,
  data: { schedule: NotificationScheduleEntry[]; defaults: Array<{ aid: number; default_sender_value: boolean }> },
  locale = 'cs',
): Promise<NotificationPreferences> {
  return mutateJson<NotificationPreferences>(baseUrl, '/notifications/preferences', data, locale, 'PUT');
}

export async function getNeoNotifications(baseUrl: string, unreadOnly = false): Promise<NeoNotification[]> {
  const query = unreadOnly ? '?unread_only=1' : '';
  return (await getJson<{ data: NeoNotification[] }>(baseUrl, `/notifications${query}`)).data;
}

export function markNeoNotificationRead(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: 'read' }> {
  return mutateJson(baseUrl, `/notifications/${id}/read`, undefined, locale);
}

export function temporarilyDisableNotification(baseUrl: string, id: number, data: { minutes?: number; until?: string }, locale = 'cs'): Promise<{ status: 'paused'; aid: number; until: string }> {
  return mutateJson(baseUrl, `/notifications/${id}/temporary-off`, data, locale);
}

export function submitFeedback(baseUrl: string, message: string, locale = 'cs'): Promise<{ status: 'sent' }> {
  return mutateJson(baseUrl, '/feedback', { message }, locale);
}

export async function getManagedUsers(baseUrl: string): Promise<ManagedUser[]> {
  return (await getJson<{ data: ManagedUser[] }>(baseUrl, '/admin/users')).data;
}

export async function getManagedRoles(baseUrl: string): Promise<{ roles: ManagedRole[]; capabilities: string[] }> {
  const response = await getJson<{ data: ManagedRole[]; capabilities: string[] }>(baseUrl, '/admin/roles');
  return { roles: response.data, capabilities: response.capabilities };
}

export function createManagedUser(baseUrl: string, data: SaveManagedUser, locale = 'cs'): Promise<{ id: number }> {
  return mutateJson(baseUrl, '/admin/users', data, locale);
}

export function updateManagedUser(baseUrl: string, id: number, data: SaveManagedUser, locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, `/admin/users/${id}`, data, locale, 'PUT');
}

export function createManagedRole(baseUrl: string, data: SaveManagedRole, locale = 'cs'): Promise<{ id: number }> {
  return mutateJson(baseUrl, '/admin/roles', data, locale);
}

export function updateManagedRole(baseUrl: string, id: number, data: SaveManagedRole, locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, `/admin/roles/${id}`, data, locale, 'PUT');
}

export function updateRoleCapabilities(baseUrl: string, id: number, capabilities: string[], locale = 'cs'): Promise<{ status: string }> {
  return mutateJson(baseUrl, `/admin/roles/${id}/capabilities`, { capabilities }, locale, 'PUT');
}

export async function getInstallations(baseUrl: string): Promise<Installation[]> {
  return (await getJson<{ installations: Installation[] }>(baseUrl, '/trainer/installations')).installations;
}

export async function getNeurons(baseUrl: string): Promise<NeuronServer[]> {
  return (await getJson<{ data: NeuronServer[] }>(baseUrl, '/settings/neurons')).data;
}

export type NewNuc = { name?: string; description?: string; ip_address?: string; res_x: number; res_y: number };
export type NewRoom = { name?: string; description?: string };
export type SensorFormValues = {
  name?: string;
  description?: string;
  type: string;
  bit?: number | null;
  bit_state: number;
  output: number;
  set_cmd?: string;
  clear_cmd?: string;
};
export type CameraFormValues = {
  name?: string; description?: string; stream_type?: string; audio_type?: string; stream_addr?: string; stream_user?: string; stream_pass?: string;
  stream_latency?: number; high?: number; lens_type?: number;
  res_x?: number; res_y?: number; poe_type?: string; poe_id?: string; poe_chan?: string; cam_enable?: number; footfall_enable?: number;
  http_stream_enable?: number; ss_stream_enable?: number; rec_enable?: number; ls_stream_enable?: number; face_enable?: number; ff_learn_rate?: number;
  adfors_enable?: number; adfors_motor_id?: number; rotate?: string; dnn_type?: string; sub_stream_addr?: string; sub_stream_type?: string; opencl_enable?: number;
};
export type CameraFlag = 'cam_enable' | 'footfall_enable' | 'http_stream_enable' | 'ss_stream_enable' | 'rec_enable' | 'ls_stream_enable';

export async function createNeuronNuc(baseUrl: string, serverId: number, data: NewNuc, locale = 'cs'): Promise<NeuronLocalServer> {
  return (await mutateJson<{ status: string; data: NeuronLocalServer }>(baseUrl, `/settings/neurons/${serverId}/nucs`, data, locale, 'POST')).data;
}

export async function createNeuronRoom(baseUrl: string, serverId: number, data: NewRoom, locale = 'cs'): Promise<NeuronRoom> {
  return (await mutateJson<{ status: string; data: NeuronRoom }>(baseUrl, `/settings/neurons/${serverId}/rooms`, data, locale, 'POST')).data;
}

export async function createNeuronSensor(baseUrl: string, roomId: number, data: SensorFormValues, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/rooms/${roomId}/sensors`, data, locale, 'POST')).data;
}

export async function createNeuronCamera(baseUrl: string, roomId: number, data: CameraFormValues, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/rooms/${roomId}/cameras`, data, locale, 'POST')).data;
}

export async function updateNeuronSensor(baseUrl: string, id: number, data: SensorFormValues, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/sensors/${id}`, data, locale, 'PUT')).data;
}

export async function updateNeuronCamera(baseUrl: string, id: number, data: CameraFormValues, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/cameras/${id}`, data, locale, 'PUT')).data;
}

export async function updateNeuronCameraFlag(baseUrl: string, id: number, flag: CameraFlag, value: number, locale = 'cs'): Promise<NeuronCamera> {
  return (await mutateJson<{ status: string; data: NeuronCamera }>(baseUrl, `/settings/neurons/cameras/${id}/flags`, { [flag]: value }, locale, 'PATCH')).data;
}

export function getNeuronCameraHttpStreamUrl(baseUrl: string, id: number): string {
  return apiUrl(baseUrl, `/settings/neurons/cameras/${id}/http-stream`);
}

export async function getCameraMasks(baseUrl: string, id: number): Promise<CameraMask[]> {
  return (await getJson<{ data: CameraMask[] }>(baseUrl, `/settings/neurons/cameras/${id}/masks`)).data;
}

export type SaveCameraMask = Omit<CameraMask, 'id' | 'cid' | 'points' | 'value' | 'description'> & {
  value?: number | null;
  description?: string | null;
  points?: CameraMaskPoint[];
};

export async function createCameraMask(baseUrl: string, id: number, data: SaveCameraMask, locale = 'cs'): Promise<CameraMask> {
  return (await mutateJson<{ status: string; data: CameraMask }>(baseUrl, `/settings/neurons/cameras/${id}/masks`, data, locale, 'POST')).data;
}

export async function updateCameraMask(baseUrl: string, cameraId: number, id: number, data: SaveCameraMask, locale = 'cs'): Promise<CameraMask> {
  return (await mutateJson<{ status: string; data: CameraMask }>(baseUrl, `/settings/neurons/cameras/${cameraId}/masks/${id}`, data, locale, 'PUT')).data;
}

export function deleteCameraMask(baseUrl: string, cameraId: number, id: number, locale = 'cs'): Promise<{ status: string; id: number }> {
  return mutateJson(baseUrl, `/settings/neurons/cameras/${cameraId}/masks/${id}`, undefined, locale, 'DELETE');
}

export async function getCameraPoints(baseUrl: string, id: number): Promise<CameraPoint[]> {
  return (await getJson<{ data: CameraPoint[] }>(baseUrl, `/settings/neurons/cameras/${id}/points`)).data;
}

export async function updateCameraPoints(baseUrl: string, id: number, points: CameraPoint[], locale = 'cs'): Promise<CameraPoint[]> {
  return (await mutateJson<{ data: CameraPoint[] }>(baseUrl, `/settings/neurons/cameras/${id}/points`, {
    points: points.map(({ p_type, abs_x, abs_y, abs_z, pix_x, pix_y }) => ({ p_type, abs_x, abs_y, abs_z, pix_x, pix_y })),
  }, locale, 'PUT')).data;
}

export async function updateNeuronRoom(baseUrl: string, id: number, data: NewRoom, locale = 'cs'): Promise<{ status: string; data: NeuronRoom }> {
  return mutateJson(baseUrl, `/settings/neurons/rooms/${id}`, data, locale, 'PUT');
}

export function deleteNeuronRoom(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string; id: number }> {
  return mutateJson(baseUrl, `/settings/neurons/rooms/${id}`, undefined, locale, 'DELETE');
}

export function deleteNeuron(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string; id: number }> {
  return mutateJson(baseUrl, `/settings/neurons/${id}`, undefined, locale, 'DELETE');
}

export async function getRoomBorder(baseUrl: string, id: number): Promise<RoomBorderPoint[]> {
  return (await getJson<{ data: RoomBorderPoint[] }>(baseUrl, `/settings/neurons/rooms/${id}/border`)).data;
}

export async function updateRoomBorder(baseUrl: string, id: number, points: RoomBorderPoint[], locale = 'cs'): Promise<RoomBorderPoint[]> {
  return (await mutateJson<{ data: RoomBorderPoint[] }>(baseUrl, `/settings/neurons/rooms/${id}/border`, { points: points.map(({ x, y }) => ({ x, y })) }, locale, 'PUT')).data;
}

export function updateNeuronNuc(baseUrl: string, id: number, data: { name?: string; description?: string; ip_address?: string; res_x: number; res_y: number }, locale = 'cs'): Promise<{ status: string; data: NeuronLocalServer }> {
  return mutateJson(baseUrl, `/settings/neurons/nucs/${id}`, data, locale, 'PUT');
}

export function deleteNeuronNuc(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string; id: number }> {
  return mutateJson(baseUrl, `/settings/neurons/nucs/${id}`, undefined, locale, 'DELETE');
}

export async function getNeuronNucTiming(baseUrl: string, id: number): Promise<NeuronTiming[]> {
  return (await getJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/nucs/${id}/timing`)).data;
}

export async function updateNeuronNucTiming(baseUrl: string, id: number, timing: SaveNeuronTiming[], locale = 'cs'): Promise<NeuronTiming[]> {
  return (await mutateJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/nucs/${id}/timing`, { timing }, locale, 'PUT')).data;
}

export async function getRoomTiming(baseUrl: string, id: number): Promise<NeuronTiming[]> {
  return (await getJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/rooms/${id}/timing`)).data;
}

export async function updateRoomTiming(baseUrl: string, id: number, timing: SaveNeuronTiming[], locale = 'cs'): Promise<NeuronTiming[]> {
  return (await mutateJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/rooms/${id}/timing`, { timing }, locale, 'PUT')).data;
}

export function getNeuronNucCameraLayout(baseUrl: string, id: number): Promise<NeuronCameraLayout> {
  return getJson(baseUrl, `/settings/neurons/nucs/${id}/camera-layout`);
}

export function updateNeuronNucCameraLayout(baseUrl: string, id: number, canvas: { width: number; height: number }, layout: SaveNeuronCameraLayoutItem[], locale = 'cs'): Promise<NeuronCameraLayout> {
  return mutateJson(baseUrl, `/settings/neurons/nucs/${id}/camera-layout`, { canvas, layout }, locale, 'PUT');
}

export function getNeuronCameraVirtual(baseUrl: string, id: number): Promise<NeuronVirtualCamera> {
  return getJson(baseUrl, `/settings/neurons/cameras/${id}/virtual`);
}

export function updateNeuronCameraVirtual(baseUrl: string, id: number, layout: Array<{ servant_cid: number; left: number; top: number; width: number; height: number }>, locale = 'cs'): Promise<NeuronVirtualCamera> {
  return mutateJson(baseUrl, `/settings/neurons/cameras/${id}/virtual`, { layout }, locale, 'PUT');
}

export function getNeuronCameraRecords(baseUrl: string, id: number): Promise<CameraRecords> {
  return getJson<{ data: CameraRecords }>(baseUrl, `/settings/neurons/cameras/${id}/records`).then((result) => result.data);
}

export function updateNeuronCameraRecords(baseUrl: string, id: number, data: SaveCameraRecords, locale = 'cs'): Promise<CameraRecords> {
  return mutateJson<{ data: CameraRecords }>(baseUrl, `/settings/neurons/cameras/${id}/records`, data, locale, 'PUT').then((result) => result.data);
}

export async function getNeuronCameraTiming(baseUrl: string, id: number): Promise<NeuronTiming[]> {
  return (await getJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/cameras/${id}/timing`)).data;
}

export async function updateNeuronCameraTiming(baseUrl: string, id: number, timing: SaveNeuronTiming[], locale = 'cs'): Promise<NeuronTiming[]> {
  return (await mutateJson<{ data: NeuronTiming[] }>(baseUrl, `/settings/neurons/cameras/${id}/timing`, { timing }, locale, 'PUT')).data;
}

export function updateNeuronPoeMode(baseUrl: string, id: number, poeMode: NeuronPoeMode, locale = 'cs'): Promise<{ status: string; id: number; poe_mode: NeuronPoeMode }> {
  return mutateJson(baseUrl, `/settings/neurons/${id}/poe-mode`, { poe_mode: poeMode }, locale, 'PUT');
}

export function applyNeuronSettings(baseUrl: string, id: number, locale = 'cs'): Promise<{ status: string; id: number; apply_settings: number }> {
  return mutateJson(baseUrl, `/settings/neurons/${id}/apply-settings`, undefined, locale, 'PUT');
}

export async function getUserReportedErrors(baseUrl: string): Promise<UserReportedError[]> {
  return (await getJson<{ data: UserReportedError[] }>(baseUrl, '/trainer/alerts/user-errors')).data;
}

export async function getUserErrorImages(baseUrl: string, id: number): Promise<ImageData[]> {
  return (await getJson<{ data: ImageData[] }>(baseUrl, `/trainer/alerts/user-errors/${id}/images`)).data;
}

export function resolveUserError(
  baseUrl: string,
  id: number,
  locale = 'cs',
): Promise<{ status: string; recipients: string[] }> {
  return mutateJson(baseUrl, `/trainer/alerts/user-errors/${id}/resolve`, undefined, locale);
}

export async function searchAlerts(
  baseUrl: string,
  params: { cs_id: number; from?: string; to?: string; classes?: number[] },
  locale = 'cs',
): Promise<ImageData[]> {
  return (await mutateJson<{ data: ImageData[] }>(baseUrl, '/trainer/alerts/search', params, locale)).data;
}

export function getAlertImageUrl(baseUrl: string, id: number): string {
  return apiUrl(baseUrl, `/trainer/alerts/image/${id}`);
}

export function submitAlertTraining(
  baseUrl: string,
  payload: AlertTrainingPayload,
  locale = 'cs',
): Promise<{ status: string }> {
  return mutateJson(baseUrl, '/trainer/alerts/train', payload, locale);
}

export async function getSuperCheckSummary(
  baseUrl: string,
  params: { cs_id?: number; from?: string; to?: string },
  locale = 'cs',
): Promise<SuperCheckGroup[]> {
  return (await mutateJson<{ groups: SuperCheckGroup[] }>(baseUrl, '/trainer/alerts/super-check/summary', params, locale)).groups;
}

export async function searchSuperCheck(
  baseUrl: string,
  params: { cs_id?: number; class_id: number; from?: string; to?: string },
  locale = 'cs',
): Promise<ImageData[]> {
  return (await mutateJson<{ data: ImageData[] }>(baseUrl, '/trainer/alerts/super-check/search', params, locale)).data;
}

export function confirmSuperCheck(
  baseUrl: string,
  imageIds: number[],
  locale = 'cs',
): Promise<{ status: string; updated: number }> {
  return mutateJson(baseUrl, '/trainer/alerts/super-check/confirm', { image_ids: imageIds }, locale);
}
